import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tabs, Typography, message } from 'antd';
import { Link } from 'react-router-dom';
import {
  addTeamMembership,
  removeTeamMembership,
  createSalesTeam,
  listMissingTeamRecords,
  listSalesTeams,
  listTeamMemberships,
  updateSalesTeam,
  type SalesTeam,
} from '@/services/salesTeam.service';
import { useUsers } from '@/hooks/useUsers';

const SYSTEM_DEFAULT_TEAM_CODE = 'IK-P';

const { Title } = Typography;

const SalesTeamsPage: React.FC = () => {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [memberForm] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [memberOpen, setMemberOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<SalesTeam | null>(null);
  const [editingTeam, setEditingTeam] = useState<SalesTeam | null>(null);

  const { data: teams = [], isLoading } = useQuery({
    queryKey: ['sales-teams'],
    queryFn: () => listSalesTeams(false),
  });

  const { data: users = [] } = useUsers();

  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
      map.set(u.id, name || u.email || u.id);
    }
    return map;
  }, [users]);

  const userSelectOptions = useMemo(
    () =>
      users
        .filter((u) => u.is_active !== false)
        .map((u) => ({
          value: u.id,
          label: userNameById.get(u.id) || u.id,
        })),
    [users, userNameById]
  );

  const { data: missingLeads = [] } = useQuery({
    queryKey: ['sales-missing-team', 'Lead'],
    queryFn: () => listMissingTeamRecords('Lead', 30),
  });
  const { data: missingOpps = [] } = useQuery({
    queryKey: ['sales-missing-team', 'Opportunity'],
    queryFn: () => listMissingTeamRecords('Opportunity', 30),
  });

  const { data: memberships = [] } = useQuery({
    queryKey: ['sales-team-memberships', selectedTeam?.id],
    queryFn: () => (selectedTeam ? listTeamMemberships(selectedTeam.id) : []),
    enabled: !!selectedTeam?.id,
  });

  const createMut = useMutation({
    mutationFn: createSalesTeam,
    onSuccess: () => {
      message.success('Sales team created');
      qc.invalidateQueries({ queryKey: ['sales-teams'] });
      qc.invalidateQueries({ queryKey: ['sales-teams-assignable'] });
      setOpen(false);
      form.resetFields();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const updateMut = useMutation({
    mutationFn: (v: { id: string; payload: Partial<SalesTeam> }) => updateSalesTeam(v.id, v.payload),
    onSuccess: (updated) => {
      message.success('Sales team updated');
      qc.invalidateQueries({ queryKey: ['sales-teams'] });
      qc.invalidateQueries({ queryKey: ['sales-teams-assignable'] });
      setEditOpen(false);
      setEditingTeam(null);
      editForm.resetFields();
      if (selectedTeam?.id === updated.id) {
        setSelectedTeam(updated);
      }
    },
    onError: (e: Error) => message.error(e.message),
  });

  const addMemberMut = useMutation({
    mutationFn: (v: { teamId: string; user_id: string; role_in_team: string }) =>
      addTeamMembership(v.teamId, { user_id: v.user_id, role_in_team: v.role_in_team }),
    onSuccess: () => {
      message.success('Member added');
      qc.invalidateQueries({ queryKey: ['sales-team-memberships', selectedTeam?.id] });
      qc.invalidateQueries({ queryKey: ['sales-teams-assignable'] });
      memberForm.resetFields();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const removeMemberMut = useMutation({
    mutationFn: removeTeamMembership,
    onSuccess: () => {
      message.success('Member removed');
      qc.invalidateQueries({ queryKey: ['sales-team-memberships', selectedTeam?.id] });
      qc.invalidateQueries({ queryKey: ['sales-teams-assignable'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Sales Teams
        </Title>
        <Space>
          <Link to="/platform/sales-team-transfers">
            <Button>Transfer register</Button>
          </Link>
          <Link to="/platform/missing-team-assignment">
            <Button>Missing team admin</Button>
          </Link>
          <Button type="primary" onClick={() => setOpen(true)}>
            New team
          </Button>
        </Space>
      </Space>

      <Table
        loading={isLoading}
        rowKey="id"
        dataSource={teams}
        columns={[
          { title: 'Code', dataIndex: 'team_code' },
          { title: 'Name', dataIndex: 'team_name' },
          { title: 'Region', dataIndex: 'region' },
          { title: 'Active', dataIndex: 'is_active', render: (v: boolean) => (v ? 'Yes' : 'No') },
          {
            title: 'Actions',
            render: (_: unknown, row: SalesTeam) => (
              <Button
                size="small"
                onClick={() => {
                  setSelectedTeam(row);
                  setMemberOpen(true);
                }}
              >
                Members
              </Button>
            ),
          },
        ]}
      />

      <Card title="Records missing team assignment" style={{ marginTop: 24 }}>
        <Tabs
          items={[
            {
              key: 'leads',
              label: `Leads (${missingLeads.length})`,
              children: (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={missingLeads}
                  pagination={false}
                  columns={[
                    { title: 'Lead #', dataIndex: 'document_number' },
                    {
                      title: '',
                      render: (_: unknown, row: { id: string }) => (
                        <Button type="link" size="small" href={`/growth/leads/${row.id}/edit`}>
                          Assign
                        </Button>
                      ),
                    },
                  ]}
                />
              ),
            },
            {
              key: 'opps',
              label: `Opportunities (${missingOpps.length})`,
              children: (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={missingOpps}
                  pagination={false}
                  columns={[
                    { title: 'Opportunity #', dataIndex: 'document_number' },
                    {
                      title: '',
                      render: (_: unknown, row: { id: string }) => (
                        <Button type="link" size="small" href={`/growth/opportunities/${row.id}/edit`}>
                          Assign
                        </Button>
                      ),
                    },
                  ]}
                />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editingTeam ? `Edit — ${editingTeam.team_name}` : 'Edit sales team'}
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditingTeam(null);
          editForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(v) => {
            if (!editingTeam) return;
            const payload: Partial<SalesTeam> = {
              team_name: v.team_name,
              region: v.region,
              business_vertical: v.business_vertical,
              description: v.description,
              manager_user_id: v.manager_user_id || null,
              is_active: v.is_active,
            };
            if (editingTeam.team_code !== SYSTEM_DEFAULT_TEAM_CODE && v.team_code) {
              payload.team_code = v.team_code;
            }
            updateMut.mutate({ id: editingTeam.id, payload });
          }}
        >
          <Form.Item name="team_code" label="Team code" rules={[{ required: true }]}>
            <Input disabled={editingTeam?.team_code === SYSTEM_DEFAULT_TEAM_CODE} />
          </Form.Item>
          <Form.Item name="team_name" label="Team name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="region" label="Region">
            <Input />
          </Form.Item>
          <Form.Item name="business_vertical" label="Business vertical">
            <Input />
          </Form.Item>
          <Form.Item name="manager_user_id" label="Team manager">
            <Select allowClear showSearch optionFilterProp="label" options={userSelectOptions} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateMut.isPending}>
            Save changes
          </Button>
        </Form>
      </Modal>

      <Modal title="New sales team" open={open} onCancel={() => setOpen(false)} footer={null}>
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
        >
          <Form.Item name="team_code" label="Team code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="team_name" label="Team name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="region" label="Region">
            <Input />
          </Form.Item>
          <Form.Item name="business_vertical" label="Business vertical">
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={createMut.isPending}>
            Save
          </Button>
        </Form>
      </Modal>

      <Modal
        title={selectedTeam ? `Members — ${selectedTeam.team_name}` : 'Members'}
        open={memberOpen}
        onCancel={() => setMemberOpen(false)}
        footer={null}
        width={720}
      >
        <Form
          form={memberForm}
          layout="inline"
          style={{ marginBottom: 16 }}
          onFinish={(v) => {
            if (!selectedTeam) return;
            addMemberMut.mutate({ teamId: selectedTeam.id, ...v });
          }}
        >
          <Form.Item name="user_id" rules={[{ required: true }]}>
            <Select
              placeholder="User"
              style={{ width: 220 }}
              showSearch
              optionFilterProp="label"
              options={userSelectOptions}
            />
          </Form.Item>
          <Form.Item name="role_in_team" initialValue="Sales Executive">
            <Select
              style={{ width: 180 }}
              options={[
                'Manager',
                'Sales Executive',
                'Estimator',
                'Sales Coordinator',
                'Sales Engineer',
                'Viewer',
              ].map((r) => ({ value: r, label: r }))}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={addMemberMut.isPending}>
            Add
          </Button>
        </Form>
        <Table
          size="small"
          rowKey="id"
          dataSource={memberships}
          columns={[
            {
              title: 'User',
              dataIndex: 'user_id',
              render: (userId: string) => userNameById.get(userId) || userId,
            },
            { title: 'Role', dataIndex: 'role_in_team' },
            { title: 'Active', dataIndex: 'is_active', render: (v: boolean) => (v ? 'Yes' : 'No') },
            {
              title: 'Actions',
              key: 'actions',
              width: 100,
              render: (_: unknown, row: { id: string }) => (
                <Popconfirm
                  title="Remove this member from the team?"
                  onConfirm={() => removeMemberMut.mutate(row.id)}
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    type="link"
                    danger
                    size="small"
                    loading={removeMemberMut.isPending && removeMemberMut.variables === row.id}
                  >
                    Remove
                  </Button>
                </Popconfirm>
              ),
            },
          ]}
          pagination={false}
        />
      </Modal>
    </div>
  );
};

export default SalesTeamsPage;
